import { zodResolver } from '@hookform/resolvers/zod';
import { type CreateLinkType, CreateLinkTypeSchema, type LinkType } from '@servicetim/shared';
import { useCreateLinkType, useUpdateLinkType } from '@servicetim/ui/api';
import { Input, toast } from '@servicetim/ui/components';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { FormDialog } from '../common/form-dialog';
import { FormField, fieldA11y } from '../common/form-field';
import { useFormSubmit } from '../common/use-form-submit';

type FormInput = z.input<typeof CreateLinkTypeSchema>;

export function LinkTypeDialog({
  linkType,
  onClose,
}: {
  linkType: LinkType | null;
  onClose: () => void;
}) {
  const id = useId();
  const create = useCreateLinkType();
  const update = useUpdateLinkType();
  const form = useForm<FormInput, unknown, CreateLinkType>({
    resolver: zodResolver(CreateLinkTypeSchema),
    defaultValues: {
      name: linkType?.name ?? '',
      outward: linkType?.outward ?? '',
      inward: linkType?.inward ?? '',
    },
  });
  const submit = useFormSubmit(form);
  const { errors } = form.formState;
  const outward = form.watch('outward') || 'outward label';
  const inward = form.watch('inward') || 'inward label';

  return (
    <FormDialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={linkType ? `Edit ${linkType.name}` : 'New link type'}
      description="A typed relation between two tickets, read from either end."
      formId={`${id}-form`}
      submitLabel={linkType ? 'Save' : 'Create'}
      size="sm"
      busy={submit.busy}
      error={submit.error}
      omitPaths={submit.omitPaths}
      onSubmit={form.handleSubmit(async (values) => {
        const saved = await submit.run(() =>
          linkType
            ? update.mutateAsync({ id: linkType.id, patch: values })
            : create.mutateAsync(values),
        );
        if (saved) {
          toast.success(linkType ? 'Link type updated' : `Link type ${saved.name} created`);
          onClose();
        }
      })}
    >
      <FormField label="Name" htmlFor={`${id}-name`} required error={errors.name?.message}>
        <Input
          {...form.register('name')}
          {...fieldA11y(`${id}-name`, { error: errors.name })}
          maxLength={40}
          placeholder="depends"
        />
      </FormField>
      <FormField
        label="Outward"
        htmlFor={`${id}-outward`}
        required
        description="Read from the source ticket."
        error={errors.outward?.message}
      >
        <Input
          {...form.register('outward')}
          {...fieldA11y(`${id}-outward`, { description: true, error: errors.outward })}
          maxLength={60}
          placeholder="depends on"
        />
      </FormField>
      <FormField
        label="Inward"
        htmlFor={`${id}-inward`}
        required
        description="Read from the target ticket."
        error={errors.inward?.message}
      >
        <Input
          {...form.register('inward')}
          {...fieldA11y(`${id}-inward`, { description: true, error: errors.inward })}
          maxLength={60}
          placeholder="is a dependency of"
        />
      </FormField>
      <p className="rounded-md bg-surface-sunken px-3 py-2 text-fg-muted text-sm">
        <span className="font-mono">ABC-1</span> {outward} <span className="font-mono">ABC-2</span>
        <span className="text-fg-muted"> · </span>
        <span className="font-mono">ABC-2</span> {inward} <span className="font-mono">ABC-1</span>
      </p>
    </FormDialog>
  );
}
